import { useCallback, useEffect } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { setDoc, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import md5 from 'md5';
import { fetchCurrentIntegrationsRequest } from 'modules/Users/store/actions';
import { hideSnackbar, showSnackbar } from 'modules/App/store/actions';
import { db } from 'firebaseConfig';
import { nullFunction } from 'utils';

const useIntegrations = () => {
  const dispatch = useDispatch();

  const user = useSelector((state) => state?.user?.user ?? {});

  const integrations = useSelector((state) => state?.user?.integrations ?? []);

  const checkIsAppIntegrated = useCallback(
    (app) => {
      const match = (integrations || []).find(({ type }) => type === app);

      if (match) {
        return true;
      }

      return false;
    },
    [integrations],
  );

  const onIntegrateSlack = useCallback(
    (e) => {
      const { checked } = e.target;

      if (checked) {
        window.location.href = `https://slack.com/oauth/v2/authorize?client_id=${process.env.REACT_APP_SLACK_CLIENT_ID}&scope=channels:join,chat:write,im:write&redirect_uri=${window.location.origin}/integration/slack`;
      } else {
        (async () => {
          try {
            await axios.delete(
              `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/integrations/slack`,
              {
                data: {
                  uid: user.uid,
                },
              },
            );

            dispatch(fetchCurrentIntegrationsRequest());
            dispatch(showSnackbar('Slack disconnected successfully.'));
            setTimeout(() => dispatch(hideSnackbar()), 5000);
          } catch (err) {
            dispatch(showSnackbar(err.toString()));
          }
        })();
      }
    },
    [dispatch, user],
  );

  const onIntegrateTrello = useCallback(
    (e) => {
      const { checked } = e.target;

      if (checked) {
        window.location.href = `https://trello.com/1/authorize?expiration=never&name=ruttl&scope=read,write&response_type=token&key=${process.env.REACT_APP_TRELLO_KEY}&return_url=${window.location.origin}/integration/trello`;
      } else {
        (async () => {
          try {
            await axios.delete(
              `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/integrations/trello`,
              {
                data: {
                  uid: user.uid,
                },
              },
            );

            dispatch(fetchCurrentIntegrationsRequest());
            dispatch(showSnackbar('Trello disconnected successfully.'));
            setTimeout(() => dispatch(hideSnackbar()), 5000);
          } catch (err) {
            dispatch(showSnackbar(err.toString()));
          }
        })();
      }
    },
    [dispatch, user],
  );

  const onIntegrateAsana = useCallback(
    (e) => {
      const { checked } = e.target;

      if (checked) {
        window.location.href = `https://app.asana.com/-/oauth_authorize?response_type=code&client_id=1201427611767194&redirect_uri=${window.location.origin}/integration/asana`;
      } else {
        (async () => {
          try {
            await axios.delete(
              `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/integrations/asana`,
              {
                data: {
                  uid: user.uid,
                },
              },
            );

            dispatch(fetchCurrentIntegrationsRequest());
            dispatch(showSnackbar('Asana disconnected successfully.'));
            setTimeout(() => dispatch(hideSnackbar()), 5000);
          } catch (err) {
            dispatch(showSnackbar(err.toString()));
          }
        })();
      }
    },
    [dispatch, user],
  );

  const onIntegrateZapier = useCallback(
    (payload) => {
      (async () => {
        try {
          if (checkIsAppIntegrated('zapier')) {
            await deleteDoc(
              doc(db, 'users', user.uid, 'integrations', md5('zapier')),
            );
            dispatch(fetchCurrentIntegrationsRequest());
            dispatch(showSnackbar('Zapier disconnected successfully.'));
          } else {
            const { config, callback = nullFunction } = payload;

            await setDoc(
              doc(db, 'users', user.uid, 'integrations', md5('zapier')),
              {
                type: 'zapier',
                config,
              },
            );
            dispatch(fetchCurrentIntegrationsRequest());
            dispatch(showSnackbar('Zapier connected successfully.'));
            callback();
          }
          setTimeout(() => dispatch(hideSnackbar()), 5000);
        } catch (e) {
          dispatch(showSnackbar('Something went wrong!'));
          setTimeout(() => dispatch(hideSnackbar()), 5000);
        }
      })();
    },
    [user, checkIsAppIntegrated, dispatch],
  );

  const onUpdateZapier = useCallback(
    (payload) => {
      (async () => {
        try {
          if (checkIsAppIntegrated('zapier')) {
            const { config, callback = nullFunction } = payload;

            await updateDoc(
              doc(db, 'users', user.uid, 'integrations', md5('zapier')),
              {
                type: 'zapier',
                config,
              },
            );
            dispatch(fetchCurrentIntegrationsRequest());
            dispatch(
              showSnackbar('Zapier configuration updated successfully.'),
            );
            callback();
            setTimeout(() => dispatch(hideSnackbar()), 5000);
          }
        } catch (e) {
          dispatch(showSnackbar('Something went wrong!'));
          setTimeout(() => dispatch(hideSnackbar()), 5000);
        }
      })();
    },
    [user, checkIsAppIntegrated, dispatch],
  );

  useEffect(() => {
    dispatch(fetchCurrentIntegrationsRequest());
  }, [dispatch]);

  return {
    integrations,
    checkIsAppIntegrated,
    onIntegrateSlack,
    onIntegrateTrello,
    onIntegrateAsana,
    onIntegrateZapier,
    onUpdateZapier,
  };
};

export default useIntegrations;
